/**
 * 運転者詳細の「本日の業務情報」の状態（ADM-25）。
 *
 * 状態は表示文字列ではなく内部値で持ち、文言と色は内部値から決める。
 * 以前は翻訳後の文字列を英語の文字列と比較して色を決めていたため、日本語表示では
 * 実施済みでも赤になり、日本語の文言も「実施済み」と「未実施」が逆だった。
 * 取得できなかった件数は「未実施」と確定せず「未取得」として表示する。
 */
export type DailyCheckState = 'done' | 'not_done' | 'unavailable';
export type AttendanceState = 'working' | 'clocked_out' | 'none' | 'unavailable';

/** 本日の点検・免許証確認の件数から状態を決める。`null` は取得できなかったこと。 */
export function dailyCheckState(count: number | null): DailyCheckState {
  if (count === null) return 'unavailable';
  return count > 0 ? 'done' : 'not_done';
}

/**
 * 最新の勤怠から状態を決める。
 * `undefined` は勤怠が0件、`null` は取得できなかったこと。
 */
export function attendanceState(
  latest: { check_out: string | null } | undefined | null
): AttendanceState {
  if (latest === null) return 'unavailable';
  if (latest === undefined) return 'none';
  return latest.check_out === null ? 'working' : 'clocked_out';
}

/** 暦日の前日（`YYYY-MM-DD`）。時間帯に依存しない（日付の文字列のまま計算する）。 */
export function previousCalendarDate(businessDate: string): string {
  const [year, month, day] = businessDate.split('-').map(Number);
  return new Date(Date.UTC(year, (month || 1) - 1, (day || 1) - 1)).toISOString().slice(0, 10);
}

/**
 * ADM-25（2026-10-11 PM 判断）。前日〜本日（会社の時間帯）の勤怠の最新1件から、本日の状態を決める。
 *
 * - 最新が本日の出勤: 未退勤は勤務中、退勤済みは退勤済み（今までどおり）。
 * - 最新が前日の出勤: 未退勤は勤務中（日をまたぐ勤務）、退勤済みは未出勤（本日は来ていない）。
 * - 前日より前の出勤（範囲の外。押し忘れなど）は勤務中にしない。
 * `latest` が `undefined` は0件、`null` は取得できなかったこと。`businessDateOf` は出勤の時刻を
 * 会社の時間帯の業務日（`YYYY-MM-DD`）にする関数。
 */
export function recentAttendanceState(
  latest: { check_in: string; check_out: string | null } | undefined | null,
  today: string,
  businessDateOf: (iso: string) => string
): AttendanceState {
  if (latest === null || latest === undefined) return attendanceState(latest);
  const day = businessDateOf(latest.check_in);
  if (day === today) return attendanceState(latest);
  if (day === previousCalendarDate(today) && latest.check_out === null) return 'working';
  return 'none';
}

const KEY = 'view.driver-detail.work-info';

export const DAILY_CHECK_LABEL_KEY: Record<DailyCheckState, string> = {
  done: `${KEY}.status-check`,
  not_done: `${KEY}.status-uncheck`,
  unavailable: `${KEY}.status-unavailable`,
};

export const ATTENDANCE_LABEL_KEY: Record<AttendanceState, string> = {
  working: `${KEY}.status-working`,
  clocked_out: `${KEY}.status-clockout`,
  none: `${KEY}.status-not-checked-in`,
  unavailable: `${KEY}.status-unavailable-attendance`,
};

const POSITIVE = 'bg-[#CEFDFF] text-black';
const NEGATIVE = 'bg-[#FFE5EE] text-[#DD0000]';
const UNKNOWN = 'bg-gray-100 text-gray-500';

export const DAILY_CHECK_STYLE: Record<DailyCheckState, string> = {
  done: POSITIVE,
  not_done: NEGATIVE,
  unavailable: UNKNOWN,
};

export const ATTENDANCE_STYLE: Record<AttendanceState, string> = {
  working: POSITIVE,
  clocked_out: NEGATIVE,
  none: NEGATIVE,
  unavailable: UNKNOWN,
};
